import React, { useEffect, useRef, useState } from 'react';
import { Layout, ConfigProvider, Drawer, Button, Avatar, theme } from 'antd';
import { Outlet, useLocation } from 'react-router-dom';
import { MenuUnfoldOutlined, MenuFoldOutlined, SunOutlined, MoonOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import Sidebar from './sidebar';
import useSidebarItems from './items';
import SupportBell from '../../components/chat/SupportBell';
import './dashboard.css';

const { Header, Content, Sider } = Layout;

export default function Dashboard() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 992);
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem('superadmin-theme');
    return saved ? saved === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const root = useRef(null);
  const location = useLocation();
  const items = useSidebarItems();
  const active = items.flatMap(item => item.children || [item]).find(item => item.link === location.pathname);
  const title = active?.label || 'Administration';
  const pageKey = location.pathname.split('/')[2] || 'home';

  useEffect(() => {
    const media = window.matchMedia('(max-width: 991px)');
    const resize = () => { setIsMobile(media.matches); setMobileOpen(false); };
    media.addEventListener('change', resize);
    return () => media.removeEventListener('change', resize);
  }, []);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  const toggleTheme = () => setDark(value => {
    localStorage.setItem('superadmin-theme', value ? 'light' : 'dark');
    return !value;
  });
  const themeButton = (compact = false) => (
    <Button type="text" className="admin-theme-toggle" onClick={toggleTheme}
      icon={dark ? <SunOutlined /> : <MoonOutlined />}
      aria-label={`Switch to ${dark ? 'light' : 'dark'} mode`} title={`Switch to ${dark ? 'light' : 'dark'} mode`}>
      {!compact && (dark ? 'Light mode' : 'Dark mode')}
    </Button>
  );
  return (
    <ConfigProvider modal={{ className: `superadmin-panel admin-modal${dark ? ' admin-dark' : ''}` }} theme={{ algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
      token: { colorPrimary: dark ? '#a3d977' : '#427a27', borderRadius: 10, fontFamily: 'Inter, system-ui, sans-serif',
        colorBgLayout: dark ? '#101510' : '#f4f6f2', colorBgContainer: dark ? '#191f19' : '#ffffff',
        colorText: dark ? '#edf2e9' : '#202b21', colorBorderSecondary: dark ? '#303a30' : '#e4e9e1' },
      components: { Menu: { itemHeight: 44, itemBorderRadius: 8 }, Button: { controlHeight: 40 }, Table: { cellPaddingBlock: 16 } }
    }} getPopupContainer={trigger => trigger?.closest('.superadmin-panel') || root.current || document.body}>
      <Layout ref={root} className="superadmin-panel" data-admin-theme={dark ? 'dark' : 'light'}>
        <a className="admin-skip-link" href="#admin-content">Skip to content</a>
        {!isMobile && <Sider className="admin-sider" width={264} collapsedWidth={80} collapsed={collapsed}>
          <Sidebar collapsed={collapsed} themeControl={themeButton(collapsed)} />
        </Sider>}
        <Drawer title="Navigation" placement="left" width={Math.min(304, window.innerWidth - 32)} open={isMobile && mobileOpen}
          onClose={() => setMobileOpen(false)} getContainer={false} className="admin-mobile-drawer" styles={{ body: { padding: 0 } }}>
          <Sidebar onNavigate={() => setMobileOpen(false)} themeControl={themeButton()} />
        </Drawer>
        <Layout className="admin-main" style={{ marginLeft: isMobile ? 0 : collapsed ? 80 : 264 }}>
          <Header className="admin-navbar">
            <div className="admin-navbar-start">
              <Button type="text" icon={collapsed || isMobile ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                aria-label={isMobile ? 'Open navigation' : collapsed ? 'Expand navigation' : 'Collapse navigation'}
                aria-expanded={isMobile ? mobileOpen : !collapsed}
                onClick={() => isMobile ? setMobileOpen(true) : setCollapsed(!collapsed)} />
              <div className="admin-breadcrumb"><span>Workspace</span><span>/</span><strong>{title}</strong></div>
            </div>
            <div className="admin-navbar-actions"><SupportBell chatPath="/dashboard/chat" />{themeButton(true)}<span className="admin-navbar-divider" />
              <Avatar icon={<SafetyCertificateOutlined />} className="admin-avatar" />
              <div className="admin-account"><strong>Superadmin</strong><span>Administration</span></div>
            </div>
          </Header>
          <Content id="admin-content" tabIndex={-1} className="admin-content">
            <div className="admin-page-heading"><div><span className="admin-eyebrow">MASTERFUT / ADMINISTRATION</span><h1>{title}</h1></div>
              <span className="admin-workspace-badge"><SafetyCertificateOutlined /> Admin workspace</span>
            </div>
            <div className={`admin-page-body admin-page-${pageKey}`}><Outlet /></div>
          </Content>
          <footer className="admin-footer">MasterFut <span>Administration workspace</span></footer>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
}
